import {MAX_SUPPORT_MESSAGE_LENGTH} from "support/supportRequestValidation";
import {utf8ByteLength} from "core/validation/utf8ByteLength";

export const WITHDRAWAL_SUBMISSION_SUBJECT = "EU withdrawal declaration";

/**
 * The declarant/contract fields the statutory declaration names — art. 11^1 alin. (4)(a)-(b) OUG
 * 34/2014, Anlage 2 zu Art. 246a § 1 Abs. 2 S. 1 Nr. 1 EGBGB. The three the consumer must be able to
 * "easily provide or confirm".
 */
export interface WithdrawalDeclarant {
	/** alin. (4)(a) "numele său" — editable, prefilled from the account's profile name. */
	name: string;
	/** Half of alin. (4)(b)'s "details identifying the contract" — the account's own address, which is
	 *  ALSO alin. (4)(c)'s "electronic means by which the confirmation... will be sent" (the api always
	 *  acknowledges to this same address; see `WithdrawalAcknowledgement` on the api side). Read-only:
	 *  displayed so the consumer can confirm it, never redirected from this form. */
	account: string;
	/** The other half of alin. (4)(b) — the plan the account is contracted to. Read-only. */
	plan: string;
}

/**
 * The TRANSMITTED declaration copy for the buyer's own locale — `account.withdrawal.transmitted` in
 * the catalogue.
 *
 * The declaration is the buyer's STATUTORY STATEMENT, so it has to be in the buyer's language
 * (kitecorp/transferify-ui#2585): a German buyer who presses „Vertrag widerrufen“ must not be recorded
 * as having said something in English, neither in the support inbox nor in the acknowledgement email
 * that quotes it back to them. That replaces the original one-language decision
 * (kitecorp/transferify-ui#255), which predates the German market.
 *
 * `declaration` renders the statement AND the declarant lines as ONE unit per locale, rather than a
 * sentence this module then pads with labelled lines, because the model forms do not share a shape:
 * Anlage 2 EGBGB and the OUG 34/2014 Anexa partea B both NAME the service inside the sentence, where
 * the English short form keeps a separate `Plan:` line. Composing the block here from per-line
 * fragments would impose one statute's shape on the others.
 */
export interface WithdrawalTransmittedCopy {
	declaration: (declarant: WithdrawalDeclarant) => string;
	/** Heading above the consumer's own optional note; never part of the statutory statement. */
	detailsHeading: string;
}

/**
 * Generous, private caps on the one field this form lets the consumer type freely — their name. The
 * account's own name is prefilled, but art. 11^1 alin. (4)(a) OUG 34/2014 lets the consumer "furnizeze"
 * (provide) it, so it must stay editable rather than a locked read-out. Bounded independently of
 * `withdrawalDetailsBudget` below so an unusually long paste here cannot starve that budget to zero.
 */
export const MAX_WITHDRAWAL_NAME_LENGTH = 300;
export const MAX_WITHDRAWAL_NAME_BYTES = 300;

/** Mirrors the support api's `@MaxUtf8Bytes(max = 2000)` message constraint. */
export function withdrawalDetailsByteLength(details: string): number {
	return utf8ByteLength(details);
}

/**
 * Admits one complete Unicode code point at a time until either bound is reached — never splits a
 * multi-byte character or a surrogate pair, unlike a naive `slice`/`substring`.
 */
function limitUtf8(value: string, maxBytes: number, maxLength: number): string {
	let admitted = "";
	let bytes = 0;
	let characters = 0;
	for (const codePoint of value) {
		const codePointBytes = utf8ByteLength(codePoint);
		if (bytes + codePointBytes > maxBytes || characters + codePoint.length > maxLength) {
			break;
		}
		admitted += codePoint;
		bytes += codePointBytes;
		characters += codePoint.length;
	}
	return admitted;
}

/** Clamps the editable declarant-name field to `MAX_WITHDRAWAL_NAME_*`. */
export function limitWithdrawalName(name: string): string {
	return limitUtf8(name, MAX_WITHDRAWAL_NAME_BYTES, MAX_WITHDRAWAL_NAME_LENGTH);
}

/** The statutory {@link WithdrawalDeclarant} fields plus the free text the consumer may add. */
export interface WithdrawalDeclarationFields extends WithdrawalDeclarant {
	/** Optional free text (order/invoice/subscription reference); not a statutory field. */
	details: string;
}

/**
 * Everything the transmitted declaration carries BEFORE the optional details block, in the buyer's
 * own locale. Factored out so `withdrawalDetailsBudget` measures EXACTLY what
 * `withdrawalSubmissionMessage` renders — including the longer German and Romanian statements, which
 * leave the consumer's note a smaller budget than the English one does.
 *
 * The name is trimmed HERE rather than in the catalogue, so the incidental whitespace rule lives in
 * one place instead of once per locale.
 */
function withdrawalPreamble(copy: WithdrawalTransmittedCopy, declarant: WithdrawalDeclarant): string {
	// The three fields by name rather than a spread of the caller's object: `withdrawalSubmissionMessage`
	// hands its whole `WithdrawalDeclarationFields` down, and a catalogue leaf must not be able to reach a
	// `details` its contract does not name.
	const {name, account, plan} = declarant;
	return copy.declaration({name: name.trim(), account, plan});
}

export interface WithdrawalDetailsBudget {
	maxBytes: number;
	maxLength: number;
}

/**
 * The support endpoint bounds the WHOLE transmitted message at 2000 UTF-8 bytes
 * (`@MaxUtf8Bytes(max = 2000)`), not just the free-text part — so admitting name/account/plan into the
 * declaration (ui#1152, to satisfy alin. (4)) means the details budget must shrink as those grow, or a
 * long declarant name could silently push a whole declaration past the api's limit and have it
 * rejected. Recomputed from the CURRENT fields rather than pinned to a fixed constant (the pre-#1152
 * shape), so the budget is exact rather than a worst-case guess.
 *
 * It is measured from the LOCALIZED copy (kitecorp/transferify-ui#2585), never from English: the German
 * and Romanian statements are the longer ones, so a budget derived from the English preamble would
 * admit a note the api then refuses — and the counter under the field would name a limit that is not
 * the real one.
 */
export function withdrawalDetailsBudget(
	copy: WithdrawalTransmittedCopy,
	declarant: WithdrawalDeclarant,
): WithdrawalDetailsBudget {
	const reserved = `${withdrawalPreamble(copy, declarant)}\n\n${copy.detailsHeading}\n`;
	return {
		maxBytes: Math.max(MAX_SUPPORT_MESSAGE_LENGTH - utf8ByteLength(reserved), 0),
		maxLength: Math.max(MAX_SUPPORT_MESSAGE_LENGTH - reserved.length, 0),
	};
}

/** Admits pasted/typed details one complete Unicode code point at a time until `budget` is spent. */
export function limitWithdrawalDetails(details: string, budget: WithdrawalDetailsBudget): string {
	return limitUtf8(details, budget.maxBytes, budget.maxLength);
}

/**
 * Whether the declaration may be confirmed: submission is permanent and needs no churn action, so only
 * a blank declarant name holds it back. The name check exists because a statutory declaration with the
 * field silently blank would carry no answer to alin. (4)(a) at all.
 *
 * It says nothing about a send in flight (kitecorp/transferify-ui#3079). The view DISABLES the confirm
 * button for an incomplete draft, and a disabled button drops the keyboard focus it holds out of the
 * dialog, so the in-flight state is reported separately and the button stays focusable through it; a
 * press meanwhile joins the send already in flight.
 */
export function isWithdrawalDraftComplete(name: string): boolean {
	return name.trim().length > 0;
}

/**
 * Builds the support-pipeline body for a statutory withdrawal declaration under art. 11^1 alin. (3)-(4)
 * OUG 34/2014 / § 356a BGB: the locale's model-form declaration, the declarant's name, and the
 * account/plan pair that identify the contract. The details block is appended only when the consumer
 * filled it in.
 *
 * This is also the exact text `WithdrawalAcknowledgementEmail` (transferify-api-service) echoes back to
 * the consumer as "What you sent" / „Was du gesendet hast“ — the api passes it through verbatim, which
 * is why composing it in the buyer's language HERE is the whole of the fix for
 * kitecorp/transferify-ui#2585: no api or email-service change is involved. Carrying name/account/plan
 * here, not just the free-text details, is what makes that durable-medium receipt a full record of
 * alin. (4)'s three fields rather than only the optional note.
 */
export function withdrawalSubmissionMessage(
	copy: WithdrawalTransmittedCopy,
	fields: WithdrawalDeclarationFields,
): string {
	const lines = [withdrawalPreamble(copy, fields)];
	const trimmedDetails = fields.details.trim();
	if (trimmedDetails.length > 0) {
		lines.push("", copy.detailsHeading, trimmedDetails);
	}
	return lines.join("\n");
}
